import ErrorIcon from "images/errorIcon.svg?react";
import { ApiRequestError } from "services/errors";
import { HStack } from "components/Stack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import React from "react";
import i18next from "i18next";
import { Trans } from "react-i18next";

const NO_ACTIVITY_TIME = 5 * 1000; // 5 secs

export class LocationType {
  static ADDRESS = "address";
  static CITIES = "(cities)";
}
export const SCHOOL_LOCATION_TYPE = LocationType.ADDRESS;

export const EMAIL_REGEX =
  /^(([^<>()\[\]\.,;:\s@"]+(\.[^<>()\[\]\.,;:\s@"]+)*)|(".+"))@(([^<>()[\]\.,;:\s@"]+\.)+[^<>()[\]\.,;:\s@"]{2,})$/;

export function getSuccessComponents(
  message: React.ReactNode,
  setToastMessage?: (msg: string | null) => void
) {
  setTimeout(() => {
    const messageElement = document.querySelector(".talaera-success-message");
    if (messageElement && setToastMessage) {
      setToastMessage(null);
    }
  }, 3000);

  return (
    <div className={"talaera-success-message"}>
      <HStack align={"center"}>
        <div className={"iconContainer"}>
          <CheckCircleIcon style={{ color: "var(--status-success-fill)" }} />
        </div>
        <span>{message}</span>
      </HStack>
    </div>
  );
}

export function getErrorComponents(respBody: any) {
  let errors: any = null;
  // ApiRequestError with server validation errors renders them via the `errors`
  // branch below; without them (5xx, offline, non-JSON body) fall back to its
  // human-readable message instead of the generic "Something went wrong".
  if (respBody instanceof ApiRequestError && !respBody.errors) {
    respBody = respBody.message;
  }
  if (respBody && respBody.statusCode) {
    respBody = respBody.errors;
  }

  if (respBody && respBody.errorComponent) {
    errors = (
      <div className="talaera-error">
        <HStack align={"center"}>
          <div className={"iconContainer"}>
            <ErrorIcon />
          </div>
          <div>{respBody.errorComponent()}</div>
        </HStack>
      </div>
    );
  } else if (respBody && respBody.errors && respBody.errors.map) {
    errors = respBody.errors.map((error: any, index: number) => (
      <div key={index} className="talaera-error">
        <HStack align={"center"}>
          <div className={"iconContainer"}>
            <ErrorIcon />
          </div>

          {error.transKey ? (
            <Trans i18nKey={error.transKey} values={error.transValues || {}}>
              {error?.description}
            </Trans>
          ) : (
            <div>{ErrorKeys[error.error_key] || error.description}</div>
          )}
        </HStack>
      </div>
    ));
  } else if (typeof respBody === "string") {
    errors = (
      <div className="talaera-error">
        <HStack align={"center"}>
          <div className={"iconContainer"}>
            <ErrorIcon />
          </div>
          <div>{respBody}</div>
        </HStack>
      </div>
    );
  } else if (React.isValidElement(respBody)) {
    errors = (
      <div className="talaera-error">
        <HStack align={"center"}>
          <div className={"iconContainer"}>
            <ErrorIcon />
          </div>
          <div>{respBody}</div>
        </HStack>
      </div>
    );
  } else {
    errors = (
      <div className="talaera-error">
        <HStack align={"center"}>
          <div className={"iconContainer"}>
            <ErrorIcon />
          </div>
          <div>
            <Trans i18nKey="Common.errors.somethingWrong" />
          </div>
        </HStack>
      </div>
    );
  }

  return errors;
}

const ErrorKeys: Record<string, string> = {
  // Todo: define Error keys according to error_key field from server
  error_key: "Something went wrong",
};

export function getErrorsString(respBody: any) {
  let errors = "";
  if (respBody instanceof ApiRequestError && !respBody.errors) {
    respBody = respBody.message;
  }
  if (respBody && respBody.statusCode) {
    respBody = respBody.errors;
  }

  if (respBody && respBody.errors) {
    respBody.errors.map((error: any) => (errors += error.description + "; "));
    errors = errors.substring(0, errors.length - 2);
  } else if (typeof respBody === "string") {
    errors = respBody;
  } else {
    errors = i18next.t("Common.errors.somethingWrong");
  }

  return errors;
}

export function getLanguageOptionText(
  lang: { native_name: string; english_name: string } | null | undefined
) {
  if (!lang) {
    return "";
  }

  return lang.native_name + " (" + lang.english_name + ")";
}

type CodeItem = { code: string };

export function findLanguageByCode<T extends CodeItem>(langList: T[], code: string) {
  const ret = langList.find(item => item.code === code);
  return ret ? ret : null;
}

export function generateGUID() {
  function s4() {
    return Math.floor((1 + Math.random()) * 0x10000)
      .toString(16)
      .substring(1);
  }
  return s4() + s4() + "-" + s4() + "-" + s4() + "-" + s4() + "-" + s4() + s4() + s4();
}

export function copyToClipboard(textNode: Node | null | undefined) {
  if (textNode) {
    const range = document.createRange();
    range.selectNodeContents(textNode);
    const selection = window.getSelection();
    if (!selection) {
      return;
    }
    selection.removeAllRanges();
    selection.addRange(range);
    document.execCommand("copy");
  }
}

export function pluralize(
  singular: string,
  plural: string,
  number: number | boolean | null | undefined
) {
  return number === 1 ? singular : plural;
}

export const capitalize = (string: string) => string[0].toUpperCase() + string.slice(1);

// TODO use structeredClone
export const cloneDeep = <T,>(data: T): T => JSON.parse(JSON.stringify(data));

export function shouldResetAutoScrollBehaviour(
  lastScrollingTime: number | null | undefined
) {
  return (
    !lastScrollingTime || new Date().getTime() - lastScrollingTime > NO_ACTIVITY_TIME
  );
}
